// ORCA360 - Transcript Combination Utility
// Combines multiple transcripts with timestamp adjustment
// Order: Briefing → Session/Simulator → Debriefing
// Timestamps are adjusted to be sequential (no overlaps)

/**
 * Transcript metadata for combination
 */
export interface TranscriptForCombine {
  id: string;
  file_name?: string | null;
  notes?: string | null;
  duration_seconds?: number | null;
  word_count?: number | null;
  processing_status?: string | null;
}

/**
 * Speaker segment from database
 */
export interface SpeakerSegment {
  transcript_id: string;
  speaker_label: string;
  start_ms: number;
  end_ms: number;
  text: string;
  start_time?: string;
  end_time?: string;
}

/**
 * Result of transcript combination
 */
export interface CombineResult {
  combinedText: string;
  totalWords: number;
  totalDuration: number; // seconds
  transcriptOffsets: Record<string, { startOffset: number; endOffset: number }>;
  sourceTranscriptIds: string[];
}

/**
 * Get the priority order for a transcript based on its name/notes
 * Returns the priority (1=briefing, 2=session, 3=debrief) or 2 (middle) as default
 *
 * IMPORTANT: Check longer/more specific keywords first to avoid substring issues
 * e.g., "debriefing" must be checked before "briefing"
 */
function getTranscriptPriority(transcript: TranscriptForCombine): number {
  const lowerName = (transcript.file_name || transcript.notes || '').toLowerCase();

  // Check keywords in order of specificity (longer keywords first)
  // This prevents "debriefing" from matching "briefing"
  const keywordsInOrder: [string, number][] = [
    // Debriefing variants (check FIRST - priority 3)
    ['debriefing', 3],
    ['debrief', 3],
    ['post-flight', 3],
    ['postflight', 3],
    ['post_flight', 3],
    ['post-sim', 3],
    ['postsim', 3],
    ['post_sim', 3],

    // Briefing variants (check AFTER debrief - priority 1)
    ['briefing', 1],
    ['brief', 1],
    ['pre-flight', 1],
    ['preflight', 1],
    ['pre_flight', 1],
    ['pre-sim', 1],
    ['presim', 1],
    ['pre_sim', 1],

    // Session/Simulator variants (priority 2)
    ['simulator', 2],
    ['session', 2],
    ['training', 2],
    ['recording', 2],
    ['flight', 2],
    ['main', 2],
    ['sim', 2],

    // Review is ambiguous but usually post-session
    ['review', 3],
  ];

  for (const [keyword, priority] of keywordsInOrder) {
    if (lowerName.includes(keyword)) {
      return priority;
    }
  }

  // Default to middle priority (session) if no keyword matches
  return 2;
}

/**
 * Sort transcripts in the correct order for combination
 * Order: Briefing (1) → Session (2) → Debriefing (3)
 * Transcripts with same priority are sorted alphabetically
 */
export function sortTranscripts<T extends TranscriptForCombine>(transcripts: T[]): T[] {
  return [...transcripts].sort((a, b) => {
    const priorityA = getTranscriptPriority(a);
    const priorityB = getTranscriptPriority(b);

    // Sort by priority first
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Same priority: sort alphabetically by file_name or notes
    const nameA = a.file_name || a.notes || '';
    const nameB = b.file_name || b.notes || '';
    return nameA.localeCompare(nameB);
  });
}

/**
 * Get the transcript type label for display
 */
export function getTranscriptTypeLabel(transcript: TranscriptForCombine): string {
  const priority = getTranscriptPriority(transcript);
  switch (priority) {
    case 1:
      return 'Briefing';
    case 3:
      return 'Debriefing';
    default:
      return 'Session';
  }
}

/**
 * Format seconds as [HH:MM:SS] timestamp
 */
function formatTimestamp(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return '[00:00:00]';
  }
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `[${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}]`;
}

/**
 * Get start time in seconds from segment
 */
function getStartSeconds(segment: SpeakerSegment): number {
  if (segment.start_ms !== null && segment.start_ms !== undefined) {
    return segment.start_ms / 1000;
  }
  const timeStr = segment.start_time || '00:00:00';
  const parts = timeStr.split(':').map((p: string) => parseInt(p, 10));
  if (parts.length === 3 && parts[0] !== undefined && parts[1] !== undefined && parts[2] !== undefined) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return 0;
}

/**
 * Get end time in seconds from segment
 */
function getEndSeconds(segment: SpeakerSegment): number {
  if (segment.end_ms !== null && segment.end_ms !== undefined) {
    return segment.end_ms / 1000;
  }
  const timeStr = segment.end_time || '00:00:00';
  const parts = timeStr.split(':').map((p: string) => parseInt(p, 10));
  if (parts.length === 3 && parts[0] !== undefined && parts[1] !== undefined && parts[2] !== undefined) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return 0;
}

/**
 * Count words in text
 */
function countWords(text: string): number {
  return text.split(/\s+/).filter((word) => word.length > 0).length;
}

/**
 * Get transcript duration from segments
 */
function getTranscriptDuration(
  transcript: TranscriptForCombine,
  segments: SpeakerSegment[]
): number {
  // Prefer explicit duration
  if (transcript.duration_seconds && transcript.duration_seconds > 0) {
    return transcript.duration_seconds;
  }

  // Calculate from segments
  const transcriptSegments = segments.filter((s) => s.transcript_id === transcript.id);
  if (transcriptSegments.length === 0) {
    return 0;
  }

  const lastSegment = transcriptSegments[transcriptSegments.length - 1];
  if (!lastSegment) {
    return 0;
  }
  return getEndSeconds(lastSegment);
}

/**
 * Combine multiple transcripts into one with adjusted timestamps
 *
 * @param transcripts - Ordered array of transcripts to combine
 * @param segments - All speaker segments for the transcripts
 * @returns Combined transcript text and metadata
 */
export function combineTranscripts(
  transcripts: TranscriptForCombine[],
  segments: SpeakerSegment[]
): CombineResult {
  const lines: string[] = [];
  let currentOffset = 0; // Running timestamp offset in seconds
  const transcriptOffsets: Record<string, { startOffset: number; endOffset: number }> = {};
  const sourceTranscriptIds: string[] = [];

  for (const transcript of transcripts) {
    const transcriptSegments = segments
      .filter((s) => s.transcript_id === transcript.id)
      .sort((a, b) => getStartSeconds(a) - getStartSeconds(b));

    const startOffset = currentOffset;
    sourceTranscriptIds.push(transcript.id);

    // Add section header
    const label = getTranscriptTypeLabel(transcript);
    const name = transcript.file_name || transcript.notes || 'Transcript';
    lines.push(`\n=== ${label.toUpperCase()}: ${name} ===`);
    lines.push(`[Offset: ${formatTimestamp(currentOffset)} | Segments: ${transcriptSegments.length}]\n`);

    for (const segment of transcriptSegments) {
      // Adjust timestamp by adding offset
      const originalSeconds = getStartSeconds(segment);
      const adjustedSeconds = originalSeconds + currentOffset;
      const adjustedTimestamp = formatTimestamp(adjustedSeconds);

      const speakerLabel = segment.speaker_label || 'Unknown';
      lines.push(`${adjustedTimestamp} ${speakerLabel}: ${segment.text}`);
    }

    // Calculate transcript duration and add to offset
    const transcriptDuration = getTranscriptDuration(transcript, segments);

    transcriptOffsets[transcript.id] = {
      startOffset,
      endOffset: startOffset + transcriptDuration,
    };

    currentOffset += transcriptDuration;
  }

  const combinedText = lines.join('\n');

  return {
    combinedText,
    totalWords: countWords(combinedText),
    totalDuration: currentOffset,
    transcriptOffsets,
    sourceTranscriptIds,
  };
}

/**
 * Validate that all transcripts have required data for combination
 */
export function validateTranscriptsForCombine(
  transcripts: TranscriptForCombine[],
  segments: SpeakerSegment[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (transcripts.length < 2) {
    errors.push('At least 2 transcripts are required for combination');
  }

  for (const transcript of transcripts) {
    const transcriptSegments = segments.filter((s) => s.transcript_id === transcript.id);
    if (transcriptSegments.length === 0) {
      const name = transcript.file_name || transcript.notes || transcript.id;
      errors.push(`Transcript "${name}" has no speaker segments`);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
